import type { TPriceId } from "@musicfy/contants/Subscriptions";

export const PENDING_CHECKOUT_PRICE_ID_KEY = "mf_pending_checkout_price_id";
export const PENDING_CHECKOUT_DISCOUNT_CODE_KEY =
  "mf_pending_checkout_discount_code";

export type PendingCheckout = {
  priceId: TPriceId;
  discountCode?: string;
};

/** Paddle price ids use the `pri_...` format. */
export const isValidPriceId = (value: unknown): value is TPriceId =>
  typeof value === "string" && /^pri_[a-z0-9]+$/i.test(value);

const isValidDiscountCode = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Z0-9_-]{2,64}$/i.test(value);

export const getPendingCheckoutPriceId = (): TPriceId | null => {
  if (typeof window === "undefined") return null;

  try {
    const value = window.sessionStorage.getItem(PENDING_CHECKOUT_PRICE_ID_KEY);
    return isValidPriceId(value) ? value : null;
  } catch {
    return null;
  }
};

export const getPendingCheckoutDiscountCode = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const value = window.sessionStorage.getItem(
      PENDING_CHECKOUT_DISCOUNT_CODE_KEY
    );
    return isValidDiscountCode(value) ? value.trim() : null;
  } catch {
    return null;
  }
};

export const getPendingCheckout = (): PendingCheckout | null => {
  const priceId = getPendingCheckoutPriceId();
  if (!priceId) return null;
  const discountCode = getPendingCheckoutDiscountCode() ?? undefined;
  return { priceId, discountCode };
};

export const setPendingCheckoutPriceId = (priceId: TPriceId): void => {
  setPendingCheckout({ priceId });
};

export const setPendingCheckout = ({
  priceId,
  discountCode,
}: PendingCheckout): void => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(PENDING_CHECKOUT_PRICE_ID_KEY, priceId);
    if (discountCode && isValidDiscountCode(discountCode)) {
      window.sessionStorage.setItem(
        PENDING_CHECKOUT_DISCOUNT_CODE_KEY,
        discountCode.trim()
      );
    } else {
      window.sessionStorage.removeItem(PENDING_CHECKOUT_DISCOUNT_CODE_KEY);
    }
  } catch {
    // Ignore unavailable storage (for example strict private browsing).
  }
};

export const clearPendingCheckoutPriceId = (): void => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(PENDING_CHECKOUT_PRICE_ID_KEY);
    window.sessionStorage.removeItem(PENDING_CHECKOUT_DISCOUNT_CODE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
};

/** Consume once so auth redirects cannot reopen checkout in a loop. */
export const takePendingCheckoutPriceId = (): TPriceId | null => {
  return takePendingCheckout()?.priceId ?? null;
};

export const takePendingCheckout = (): PendingCheckout | null => {
  const pending = getPendingCheckout();
  if (pending) {
    clearPendingCheckoutPriceId();
  }
  return pending;
};
